import apiLogger from "../utils/apiLogger";
import { generateTraceId } from "../utils/uuid";
import { getClientIp, getAppReferer } from "../utils/clientInfo";
import {
  ENV,
  getAccessKey,
  getActionKey,
  isProxyEnabled,
  isMockDataEnabled,
  getGatewayUrl,
} from "../config/env";

interface ServerGroupResponse {
  status: number;
  code: string;
  message: string;
  data?: {
    id: number;
    centerId: number;
    centerid: string;
    serverGroup: string;
    subscribe: string;
    serviceOptions: string;
    chatProviders: string;
    license: string;
    licenseCount: number;
    ipccAddr: string;
    ipccDomain: string;
    dashDomain: string;
    ctiDomain: string | null;
    externalCenterid: string;
  };
  remark?: string | null;
}

interface LoginResponse {
  status: number;
  code: string;
  message: string;
  data?: {
    result: boolean;
    User: {
      id: number;
      centerId: number;
      groupId: number;
      teamId: number;
      userId: number;
      sippeerId: number;
      userid: string;
      name: string;
      email: string;
      role: string;
    };
  };
  remark?: string | null;
}

interface LoginCredentials {
  centerId: string;
  loginId: string; // Can be either userid or email
  password: string;
}

interface AllCentersResponse {
  status: number;
  code: string;
  message: string;
  data?: {
    count: number;
    rows: Array<{
      id: number;
      centerId: number;
      centerid: string;
      centerName: string;
      serverGroup: string;
      subscribe: string;
      serviceOptions: string;
      chatProviders: string;
      license: string;
      licenseCount: number;
      ipccAddr: string;
      ipccDomain: string;
      dashDomain: string;
      ctiDomain: string;
      externalCenterid: string;
      memo?: string;
      active: boolean;
      createdAt: string;
      updatedAt: string;
      deletedAt?: string;
    }>;
  };
  remark?: string | null;
}

class AuthService {
  constructor() {
    // Log configuration status on initialization
    const accessKey = getAccessKey();
    const actionKey = getActionKey();
    console.log("AuthService initialized with environment config:", {
      accessKey: accessKey ? "***" + accessKey.slice(-4) : "NOT SET",
      actionKey: actionKey ? "***" + actionKey.slice(-4) : "NOT SET",
      useMockData: isMockDataEnabled(),
      useProxy: isProxyEnabled(),
    });
  }

  /**
   * Get server group information from center ID
   * API: POST https://desk.matrixcloud.kr/matrixGw/center-operations/server-group
   */
  async getServerGroup(
    centerId: string
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const traceId = generateTraceId();
      const normalizedCenterId = centerId.trim(); // Don't force lowercase - API may be case-sensitive

      // Actual API call to MatrixCloud Gateway
      // Use proxy path when USE_PROXY is true (for CORS in development)
      //const useProxy = isProxyEnabled();
      const endpoint = `${getGatewayUrl()}/matrixGw/center-operations/server-group`;

      const accessKey = getAccessKey();

      // Debug: Log the actual key value
      console.log("[DEBUG] Access Key Check:", {
        exists: !!accessKey,
        isEmpty: accessKey === "",
        length: accessKey ? accessKey.length : 0,
        value:
          accessKey && accessKey.length > 0
            ? `${accessKey.substring(0, 10)}...${accessKey.slice(-4)}`
            : "EMPTY or NOT SET",
        fullValue: accessKey || "EMPTY STRING", // Temporary for debugging
      });

      if (!accessKey || accessKey.length === 0) {
        console.error(
          "⚠️ CRITICAL: Access key is empty! Check ENV configuration"
        );
        console.error("ENV.ACCESS_KEY:", ENV.ACCESS_KEY || "NOT SET");
      }

      const requestHeaders = {
        "Content-Type": "application/json",
        "trace-id": traceId,
        "access-key": accessKey,
      };

      apiLogger.logRequest(endpoint, "POST", {
        centerid: normalizedCenterId,
        headers: {
          "trace-id": traceId,
          "access-key":
            accessKey && accessKey.length > 0
              ? "***" + accessKey.slice(-4)
              : "NOT SET or EMPTY",
        },
      });

      console.log("Sending server group request:", {
        endpoint: endpoint,
        headers: requestHeaders,
        body: { centerid: normalizedCenterId },
      });

      const response = await fetch(endpoint, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify({
          centerid: normalizedCenterId, // Use 'centerid' (lowercase) as per API spec
        }),
      });

      const data: ServerGroupResponse = await response.json();

      apiLogger.logResponse(endpoint, response.status, data);

      // Check for success based on the response format
      if (!response.ok || data.code !== "SUCCESS" || !data.data) {
        return {
          success: false,
          message: data.message || "서버 그룹 조회에 실패했습니다.",
        };
      }

      // Transform response to our internal format
      // Use dashDomain as the primary server URL for the center
      return {
        success: true,
        data: {
          serverGroup: data.data.serverGroup,
          serverUrl: data.data.dashDomain || data.data.ipccDomain,
          centerName: data.data.centerid,
          centerId: data.data.centerId,
          ipccDomain: data.data.ipccDomain,
          dashDomain: data.data.dashDomain,
          license: data.data.license,
          licenseCount: data.data.licenseCount,
          serviceOptions: data.data.serviceOptions,
          chatProviders: data.data.chatProviders,
          rawData: data.data,
        },
      };
    } catch (error) {
      console.error("Error getting server group:", error);
      apiLogger.logError(
        "server-group",
        "네트워크 오류가 발생했습니다.",
        error
      );
      return {
        success: false,
        message: "네트워크 오류가 발생했습니다.",
      };
    }
  }

  /**
   * Login with center ID (two-step process)
   * 1. Get server group from center ID
   * 2. Login using the server group's API endpoint
   */
  async loginWithCenter(credentials: LoginCredentials): Promise<{
    success: boolean;
    user?: any;
    token?: string;
    refreshToken?: string;
    message?: string;
  }> {
    try {
      // Step 1: Get server group information
      const serverGroupResponse = await this.getServerGroup(
        credentials.centerId
      );

      if (!serverGroupResponse.success || !serverGroupResponse.data) {
        return {
          success: false,
          message:
            serverGroupResponse.message || "서버 그룹 조회에 실패했습니다.",
        };
      }

      const { serverGroup, serverUrl, centerName } = serverGroupResponse.data;

      // Step 2: Login using the server-specific endpoint
      // Determine if loginId is email or userid
      const isEmail = credentials.loginId.includes("@");
      const clientIp = await getClientIp();
      const traceId = generateTraceId();
      const useMockData = isMockDataEnabled();

      if (useMockData) {
        // Development mode: use mock authentication
        const loginEndpoint = `/matrixIpcc/${serverGroup}/api/apps/login`;
        apiLogger.logRequest(loginEndpoint, "POST", {
          serverGroup,
          centerId: serverGroupResponse.data.centerId || 1,
          userid: !isEmail ? credentials.loginId : null,
          email: isEmail ? credentials.loginId : null,
          password: credentials.password,
          headers: {
            "trace-id": traceId,
            "action-key": "***",
            "client-ip": clientIp,
          },
        });

        await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate network delay

        const users = JSON.parse(localStorage.getItem("users") || "[]");
        const foundUser = users.find((u: any) => {
          if (isEmail) {
            return (
              u.email === credentials.loginId &&
              u.password === credentials.password
            );
          } else {
            // For development, userid can be the same as email without @
            return (
              (u.userid === credentials.loginId ||
                u.email.split("@")[0] === credentials.loginId) &&
              u.password === credentials.password
            );
          }
        });

        if (!foundUser) {
          const errorMsg = "아이디 또는 비밀번호가 올바르지 않습니다.";
          apiLogger.logError(loginEndpoint, errorMsg, {
            loginId: credentials.loginId,
          });
          return {
            success: false,
            message: errorMsg,
          };
        }

        // Mock the actual API response format
        const mockLoginResponse: LoginResponse = {
          status: 200,
          code: "SUCCESS",
          message: "Logged in successfully",
          data: {
            result: true,
            User: {
              id: parseInt(foundUser.id),
              centerId: serverGroupResponse.data.centerId || 1,
              groupId: 0,
              teamId: 0,
              userId: parseInt(foundUser.id),
              sippeerId: 0,
              userid: foundUser.userid || foundUser.email.split("@")[0],
              name: foundUser.name,
              email: foundUser.email,
              role: foundUser.role || "AGENT", // Mock data uses AGENT as default
            },
          },
        };

        // Generate mock token (in real API, this comes from response header)
        const appsToken = this.generateMockToken(mockLoginResponse.data!.User);

        // Map API role to application role for mock data
        let mappedRole = "general_admin"; // Default to ADMIN
        if (mockLoginResponse.data!.User.role === "SYSTEM") {
          mappedRole = "system_admin";
        } else if (mockLoginResponse.data!.User.role === "AGENT") {
          mappedRole = "general_user";
        }

        // Create enriched user object for our app
        const userWithCenter = {
          ...mockLoginResponse.data!.User,
          role: mappedRole,
          centerId: credentials.centerId.toLowerCase(),
          centerInfo: {
            centerId: credentials.centerId.toLowerCase(),
            centerName,
            serverGroup,
            serverUrl,
          },
          serverGroup,
          serverUrl,
        };

        // Store authentication information
        localStorage.setItem("serverGroup", serverGroup);
        localStorage.setItem("serverUrl", serverUrl);
        localStorage.setItem("centerId", credentials.centerId.toLowerCase());
        localStorage.setItem("apps-token", appsToken);

        apiLogger.logResponse(loginEndpoint, 200, mockLoginResponse);

        return {
          success: true,
          user: userWithCenter,
          token: appsToken,
          refreshToken: this.generateMockRefreshToken(userWithCenter),
        };
      }

      // Actual login API call
      //const useProxy = isProxyEnabled();
      const loginEndpoint = `${getGatewayUrl()}/matrixIpcc/${serverGroup}/api/apps/login`;

      const actionKey = getActionKey();
      const accessKey = getAccessKey();

      // Debug: Check if keys are different
      console.log("[DEBUG] Login Keys Check:");
      console.log(
        "  ACCESS_KEY:",
        accessKey
          ? `${accessKey.substring(0, 10)}...${accessKey.slice(-4)}`
          : "NOT SET"
      );
      console.log(
        "  ACTION_KEY:",
        actionKey
          ? `${actionKey.substring(0, 10)}...${actionKey.slice(-4)}`
          : "NOT SET"
      );
      console.log("  Keys are different:", accessKey !== actionKey);

      const loginHeaders = {
        "Content-Type": "application/json",
        "trace-id": traceId,
        "action-key": actionKey,
        "client-ip": clientIp,
      };

      apiLogger.logRequest(loginEndpoint, "POST", {
        serverGroup,
        centerId: serverGroupResponse.data.centerId || 1,
        userid: !isEmail ? credentials.loginId : null,
        email: isEmail ? credentials.loginId : null,
        password: "***",
        referer: getAppReferer(),
        headers: {
          "trace-id": traceId,
          "action-key": actionKey ? "***" + actionKey.slice(-4) : "NOT SET",
          "client-ip": clientIp,
        },
      });

      console.log("Sending login request with headers:", {
        ...loginHeaders,
        "action-key": actionKey ? "***" + actionKey.slice(-4) : "NOT SET",
      });

      const loginResponse = await fetch(loginEndpoint, {
        method: "POST",
        headers: loginHeaders,
        body: JSON.stringify({
          serverGroup,
          centerId: serverGroupResponse.data.centerId || 1,
          userid: !isEmail ? credentials.loginId : null,
          email: isEmail ? credentials.loginId : null,
          password: credentials.password,
          expiresIn: "7d",
          referer: getAppReferer(),
        }),
      });

      const loginData: LoginResponse = await loginResponse.json();
      console.log("loginData", loginResponse.headers);

      // Get apps-token from response header
      const appsToken = loginResponse.headers.get("apps-token");
      console.log("appsToken", appsToken);
      apiLogger.logResponse(loginEndpoint, loginResponse.status, {
        ...loginData,
        "apps-token": appsToken ? "***hidden***" : null,
      });

      if (
        !loginResponse.ok ||
        loginData.code !== "SUCCESS" ||
        !loginData.data?.result
      ) {
        return {
          success: false,
          message: loginData.message || "로그인에 실패했습니다.",
        };
      }

      // Store server and user information
      if (loginData.data && appsToken) {
        const { User } = loginData.data;

        // Map API role to application role
        let mappedRole = "general_admin"; // Default to ADMIN
        if (User.role === "SYSTEM") {
          mappedRole = "system_admin";
        } else if (User.role === "AGENT") {
          mappedRole = "general_user";
        }

        const enrichedUser = {
          ...User,
          role: mappedRole,
          centerId: credentials.centerId.toLowerCase(),
          centerInfo: {
            centerId: credentials.centerId.toLowerCase(),
            centerName,
            serverGroup,
            serverUrl,
          },
          serverGroup,
          serverUrl,
        };

        localStorage.setItem("serverGroup", serverGroup);
        localStorage.setItem("serverUrl", serverUrl);
        localStorage.setItem("centerId", credentials.centerId.toLowerCase());
        localStorage.setItem("apps-token", appsToken);

        return {
          success: true,
          user: enrichedUser,
          token: appsToken,
          refreshToken: appsToken, // Using same token as refresh for now
        };
      }

      return {
        success: false,
        message: "로그인 토큰을 받지 못했습니다.",
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: "로그인 중 네트워크 오류가 발생했습니다.",
      };
    }
  }

  /**
   * Generate a mock JWT token
   */
  private generateMockToken(user: any): string {
    const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
    const payload = btoa(
      JSON.stringify({
        sub: user.id,
        email: user.email,
        centerId: user.centerId,
        serverGroup: user.serverGroup,
        exp: Date.now() + 3600000, // 1 hour
      })
    );
    const signature = btoa(getAccessKey() + "." + header + "." + payload);

    return `${header}.${payload}.${signature}`;
  }

  /**
   * Generate a mock refresh token
   */
  private generateMockRefreshToken(user: any): string {
    const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
    const payload = btoa(
      JSON.stringify({
        sub: user.id,
        type: "refresh",
        centerId: user.centerId,
        exp: Date.now() + 604800000, // 7 days
      })
    );
    const signature = btoa(
      getAccessKey() + ".refresh." + header + "." + payload
    );

    return `${header}.${payload}.${signature}`;
  }

  /**
   * Verify token validity
   */
  verifyToken(token: string): boolean {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      return payload.exp > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Get all centers (for system admin users)
   * API: GET https://desk.matrixcloud.kr/matrixGw/center-operations/all
   */
  async getAllCenters(): Promise<{
    success: boolean;
    data?: any[];
    message?: string;
  }> {
    try {
      const traceId = generateTraceId();
      //const useProxy = isProxyEnabled();
      const endpoint = `${getGatewayUrl()}/matrixGw/center-operations/all`;

      const accessKey = getAccessKey();

      const requestHeaders = {
        "Content-Type": "application/json",
        "trace-id": traceId,
        "access-key": accessKey,
      };

      console.log("Fetching all centers:", { endpoint });

      const response = await fetch(endpoint, {
        method: "GET",
        headers: requestHeaders,
      });

      const data: AllCentersResponse = await response.json();

      apiLogger.logResponse(endpoint, response.status, data);

      if (!response.ok || data.code !== "SUCCESS" || !data.data) {
        return {
          success: false,
          message: data.message || "센터 목록 조회에 실패했습니다.",
        };
      }

      // Transform to simplified format
      const centers = data.data.rows.map((row) => ({
        id: row.centerid,
        name: row.centerName,
        label: `${row.centerName}(${row.centerid})`,
        serverGroup: row.serverGroup,
        serverUrl: row.dashDomain,
        rawData: row,
      }));

      return {
        success: true,
        data: centers,
      };
    } catch (error) {
      console.error("Error fetching all centers:", error);
      return {
        success: false,
        message: "네트워크 오류가 발생했습니다.",
      };
    }
  }
}

const authService = new AuthService();
export default authService;
